/**
 * `/onboarding` and `/onboarding/dashboard` are FIRST-RUN ONLY.
 *
 * Once a workspace has a connected business account it is finished with both:
 * the questions personalize a setup plan it no longer needs, and the plan
 * itself is a checklist telling the user to do the thing they have already
 * done. The gate in App.tsx stopped *sending* people here, but nothing stopped
 * them arriving — a bookmark, browser history, the back button after
 * connecting, or a typed URL.
 *
 * ── Why "has a business account" is the right signal ────────────────────────
 *
 * The obvious alternative — `onboarding_completed` — cannot work: reaching the
 * plan CALLS `markOnboardingComplete()` on mount, so the flag is true from the
 * user's very first visit onward. Gating on it would lock the pages on their
 * own second render and make first-run setup unreachable for exactly the person
 * it exists for. A connected business account is the only signal that is false
 * during first run and true afterwards, and it is also the thing the plan is
 * asking the user to go and do.
 *
 * ── Why the accounts come from the HOOK, not the context ────────────────────
 *
 * `/onboarding` renders in a deliberately BARE shell — no AppShell, no
 * `BusinessAccountProvider` — and that is test-locked. Wrapping it in the
 * provider to satisfy this guard would also drag in the provider's full-screen
 * "Loading Dashboard… please wait while we load your business accounts" state,
 * which is exactly the wrong thing to show on a first-run question screen.
 * `useBusinessAccounts()` needs only `useAuth` + the QueryClient, both of which
 * sit above the routes, and it reads the SAME `business-accounts-v2` query key
 * the provider uses — so there is no duplicate request either way.
 */

import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';

/** Where a user who is past first run belongs. */
export const FIRST_RUN_FALLBACK_PATH = '/home';

export type FirstRunRouteAccess = 'pending' | 'allow' | 'block';

/**
 * Decide from the business-account list alone. Pure, so the three states can be
 * unit tested without a provider or a query client.
 *
 * The three cases are deliberately NOT a boolean:
 *
 *  - **`pending`** — the query has not settled. Deciding here is the bug this
 *    codebase has already hit twice (see BusinessAccountContext's own isLoading
 *    comment): React Query reports `isLoading === false` while a query is
 *    *disabled*, which is why the context ORs in `isPending`. Reading an
 *    unsettled list as "no accounts" would let a returning user through; the
 *    reverse — reading it as "has accounts" — would bounce a brand-new user out
 *    of the only pages that can set them up.
 *
 *  - **`allow`** — a DEFINITE empty list. This is first run.
 *
 *  - **`block`** — a DEFINITE non-empty list. Past first run.
 *
 * Anything else (`null`/`undefined` while not loading, or a non-array) means we
 * cannot know, and resolves to **allow**: this guard's restrictive action is
 * the redirect, so an unknown must never trigger it. Failing open costs a
 * returning user one stale page they can navigate away from; failing closed
 * would strand a new customer with no way to set their workspace up at all.
 */
export function resolveFirstRunRouteAccess({
  accounts,
  isLoading,
}: {
  accounts: unknown;
  isLoading: boolean;
}): FirstRunRouteAccess {
  if (isLoading) return 'pending';
  if (!Array.isArray(accounts)) return 'allow';
  return accounts.length > 0 ? 'block' : 'allow';
}

/**
 * The verdict is taken ONCE and then frozen for the lifetime of the mount.
 *
 * This is what makes connecting an account ON these pages safe. The Meta modal
 * lives on the setup plan; a successful connect invalidates
 * `business-accounts-v2`, so the list flips from empty to non-empty while the
 * user is still standing there. A live verdict would fire the redirect mid-flow
 * and pull the page out from under them — and worse, it would beat the modal's
 * own `navigate('/home/channels/whatsapp')`, dumping them on Overview instead
 * of the channel they just connected.
 *
 * Freezing means: whoever was allowed in stays in until they navigate away.
 * The next visit is a fresh mount and gets a fresh verdict, which is exactly
 * when the block should start applying.
 */
export function OnboardingFirstRunGuard({ children }: { children: React.ReactNode }) {
  // `isPending` is OR-ed in for the same reason BusinessAccountContext does it:
  // React Query reports `isLoading === false` while the query is DISABLED
  // (which it is until the auth session hydrates), and treating that as settled
  // would decide off an empty list that had never been fetched.
  const { data: accounts, isLoading, isPending } = useBusinessAccounts();
  const [verdict, setVerdict] = React.useState<FirstRunRouteAccess>('pending');

  React.useEffect(() => {
    // Frozen — see above.
    if (verdict !== 'pending') return;
    const next = resolveFirstRunRouteAccess({
      accounts,
      isLoading: Boolean(isPending) || isLoading,
    });
    if (next !== 'pending') setVerdict(next);
  }, [accounts, isLoading, isPending, verdict]);

  // Render NOTHING while undecided rather than the page. Painting a first-run
  // screen and snatching it away a frame later is worse than a brief blank, and
  // this sits outside AppShell so there is no chrome to flash either.
  if (verdict === 'pending') return null;

  if (verdict === 'block') {
    return <Navigate to={FIRST_RUN_FALLBACK_PATH} replace />;
  }

  return <>{children}</>;
}
